/**
 * 여권테스트 (tools) — 여권 QR 발급 · 부여 · 취소안내
 * ============================================================
 * 기획서 : docs/2026-08-28-tools-passport-test-design.md §4.1(3탭) · §4.2(발급) · §4.3(인쇄) · §4.3.1(코드 목록) · §4.4(부여 5콜)
 * dev-steps: 동 -dev-steps.md "### S5." (골격 + 발급 탭) · "### S6." (인쇄 + 호스트 게이트) · "### S7." (부여 5콜)
 *
 * 왜 이 화면이 있는가: 여권 QR 의 발급 → 인쇄 → 앱 부여 → 취소 왕복을 ITHUB 개발자·QA 가
 * 현장 장비 없이 되돌려 볼 수 있어야 한다. 이 화면은 **테스트 도구**이며 운영 업무 화면이 아니다.
 *
 * 탭 구성 (기획 §4.1)
 *   ① 발급     지점 선택 → 10장 발행 → 코드 10건 표시
 *   ② 부여     전화번호 + 코드로 **앱과 동일한 엔드포인트 5개**를 앱과 같은 순서로 호출 (R4)
 *   ③ 취소안내 기존 `/passport-cancel` 로 링크만 — 신규 개발 0 (R5)
 *
 * 🔴 **화면이 수량을 정하지 않습니다.** 10장은 서버 상수(`ISSUE_QUANTITY`)이고 요청 본문에
 *    수량 필드가 없다(`IssueRequest` 가 `extra="forbid"`). 버튼 문구의 "10장" 은 서버 약속의
 *    표시일 뿐이며, 화면이 보내는 값이 아니다.
 *
 * 🔴 **환경 게이트 거부는 HTTP 403 입니다** (기획 §4.2). tools 의 다른 TEST 게이트는
 *    200 + `{"success": false}` 를 내지만 이 API 는 의도적으로 이탈했다. 따라서 거부는
 *    `catch` 로 들어온다 — 다른 화면의 `res.success === false` 처리 패턴을 복사하면
 *    거부가 조용히 성공으로 보인다.
 *
 * 🔴 **서버 오류 메시지를 가공하지 않습니다** (§7 E1). `coupon_on=0` 지점 발행 실패처럼
 *    운영자가 스스로 풀 수 있는 실패는 서버 문구가 그대로 보여야 한다.
 *
 * 🔴 **인쇄 호스트는 서버 값으로만 판정합니다** (§4.3 E9 / C11). 라벨의 QR 은 서버의
 *    `WEBAPP_BASE_URL` 로 구워지므로, 화면이 `process.env` 로 같은 값을 다시 계산하면
 *    조용히 갈라져 스캔 불가한 라벨이 나온다. 판정 근거는 응답의 `webapp_base` 뿐이다.
 *
 * 🔴 **부여 탭은 공용 `api` 를 쓰지 않습니다** (§4.4 ERR-4). 공용 인스턴스는 요청을 가로채
 *    `Authorization` 을 STAFF JWT 로 덮어쓰므로 ③④⑤가 고객이 아닌 스태프 신원으로 나가고,
 *    401 을 만나면 `/login` 으로 이동시켜 테스트 화면이 통째로 날아간다. 아래 `grantHttp`
 *    참조.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import api, { API_BASE_URL, getStores, printPassportTestBatch } from '../services/api';
import StoreBadge from '../components/StoreBadge';
import './PassportTestPage.css';

/**
 * 직전 배치 복원용 키 (§7 E8).
 * 인쇄 도중 브라우저를 닫아도 코드를 회수할 수 있어야 한다. 저장하는 것은 `batch_id` **뿐**이며
 * 코드·`webapp_base` 는 절대 캐시하지 않는다 — 서버 값과 조용히 갈라지는 C11 그 자체가 된다.
 */
const STORAGE_KEY_BATCH_ID = 'passport_test_batch_id';

const TABS = [
  { id: 'issue', label: '발급' },
  { id: 'grant', label: '부여' },
  { id: 'cancel', label: '취소안내' },
];

/** `passport_qr_codes.status` 표시 문구 — 값은 서버 enum 그대로다(`PassportCancelPage` 와 동일). */
const CODE_STATUS_LABELS = {
  available: '미등록',
  registered: '등록됨',
  void: '폐기',
};

/**
 * 서버 오류를 화면 문구로 바꿉니다.
 * `services/api.js` 인터셉터가 오류를 평면 객체로 정규화하므로 `err.message` 를 읽습니다
 * (`err.response.data.message` 가 아닙니다).
 */
const errorText = (err, fallback) => err?.message || fallback;

/**
 * 부여 탭 전용 HTTP 클라이언트 (기획 §4.4 ERR-4 / dev-steps S7).
 *
 * 🔴 **공용 `api` 로는 이 체인이 성립하지 않는다.** 공용 인스턴스는 요청을 가로채
 *    `Authorization` 을 STAFF JWT 로 **무조건 덮어쓰므로**, 요청별 헤더로 모바일 토큰을
 *    넘겨도 교체돼 ③④⑤가 고객이 아닌 스태프 신원으로 나간다. 게다가 401 을 만나면
 *    `/login` 으로 이동시켜 화면이 통째로 날아간다 — 401 이 바로 이 도구가 보여줘야 할
 *    결과인데 그것을 보기 전에 페이지가 사라진다.
 *
 * 그래서 여기서는 **가로채는 단계가 하나도 없는** 별도 인스턴스를 쓴다. 결과적으로
 *   · 응답 언랩이 없다 → **`res.data.access_token`** 으로 읽는다 (공용은 `res.access_token`)
 *   · 오류는 axios 원형 그대로다 → `err.response.status` / `err.response.data.message`
 *   · 401 을 받아도 리다이렉트하지 않고 그 단계의 실패로 화면에 남는다
 *
 * `baseURL` 은 공용과 **같은 값**이어야 한다 — 컨테이너 빌드가 `REACT_APP_API_URL=/api/v1`
 * 상대경로 + nginx 프록시 **짝**으로 동작하므로 값을 복제하면 조용히 갈라진다. 그래서
 * `api.js` 가 export 한 상수를 그대로 받아 쓴다(복제 금지).
 *
 * `withCredentials` 는 켜지 않는다 — 스태프 refresh 쿠키를 고객용 앱 경로에 실어 보낼
 * 이유가 없다.
 */
const grantHttp = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

/**
 * 부여 5콜 (기획 §4.4 / dev-steps §4.2). **순서와 개수가 계약이다** — R4("앱과 동일한
 * 엔드포인트")를 지키려고 앱이 부르는 경로를 앱이 부르는 순서로 부른다.
 * ①② 는 앱의 소셜 로그인을 대신하는 dev 전용 준비 단계이고, ③④⑤ 가 앱 경로다.
 *
 * ③ `validate` 는 2026-08-28 커밋 `027c727` 로 앱 계약에 추가된 사전검증이며 **읽기 전용**이라
 * 여러 번 눌러도 안전하다.
 */
const GRANT_STEPS = [
  { id: 'login', seq: '①', title: 'dev 로그인 티켓', call: 'POST /mobile-auth/dev-login-by-phone', auth: '무인증' },
  { id: 'exchange', seq: '②', title: '토큰 교환', call: 'POST /mobile-auth/exchange', auth: '무인증' },
  { id: 'validate', seq: '③', title: 'QR 사전검증', call: 'POST /app/passport-registrations/validate', auth: '모바일 Bearer' },
  { id: 'family', seq: '④', title: '가족 조회', call: 'GET /app/family', auth: '모바일 Bearer' },
  { id: 'register', seq: '⑤', title: '여권 부여', call: 'POST /app/passport-registrations', auth: '모바일 Bearer' },
];

const STEP_ICONS = { idle: '·', running: '…', ok: '✅', fail: '⚠️' };

/** 5단계 전부 미실행 상태. 다시 실행할 때마다 이 값으로 되돌린다(직전 결과를 끌고 가지 않는다). */
const IDLE_STEPS = GRANT_STEPS.reduce(
  (acc, step) => ({ ...acc, [step.id]: { status: 'idle', detail: null } }),
  {},
);

/**
 * 부여 5콜의 오류 문구. 공용 `api` 가 만들어 주는 평면 오류 객체가 아니라 **axios 원형**을
 * 받는다 — 이 클라이언트에는 오류를 정규화하는 단계가 없다.
 *
 * 상태코드를 붙이는 이유: 같은 사람이 봐도 403(대상이 본인·자녀가 아님)과 409(가족 밖
 * 회원이 이미 선점)는 전혀 다른 사고이고, 테스트 도구의 값은 그 구분에 있다.
 * FastAPI 검증 오류의 `detail` 은 문자열이 아니라 배열일 수 있어 문자열일 때만 쓴다.
 */
const grantErrorText = (err, fallback) => {
  const status = err?.response?.status;
  const body = err?.response?.data;
  const message = typeof body?.message === 'string'
    ? body.message
    : (typeof body?.detail === 'string' ? body.detail : null);
  if (message) return status ? `${message} (HTTP ${status})` : message;
  if (status) return `${fallback} (HTTP ${status})`;
  return err?.message || fallback;
};

/**
 * 티켓·토큰을 **앞뒤 6자씩** 보여준다 — 네트워크 탭의 Bearer 값과 눈으로 대조하기 위한 값이다(V11).
 *
 * 🔴 앞부분만 보여주면 쓸모가 없다. JWT 는 전부 `eyJhbGci…` 로 시작해서 STAFF JWT 와 모바일
 *    access_token 이 앞 8자가 **완전히 같다** — 대조가 목적인데 구분이 안 된다. 서로 다른 것은
 *    꼬리(서명)다. 반대로 ①의 티켓은 불투명 난수라 머리가 구분점이다. 그래서 양끝을 쓴다.
 */
const maskPreview = (value) => (
  typeof value === 'string' && value.length > 16
    ? `${value.slice(0, 6)}…${value.slice(-6)}`
    : String(value ?? '')
);

/**
 * ④ 응답을 부여 대상 후보로 편다. **기본 선택은 본인**이며(기획 §4.4), 자녀가 0명이어도
 * 오류가 아니다 — 본인 1명으로 정상 진행한다(E10).
 */
const buildTargets = (family) => {
  const parent = family?.parent;
  const parentOption = parent?.member_id
    ? [{ member_id: parent.member_id, label: `본인 · ${parent.nickname || parent.name || parent.member_id}` }]
    : [];
  const children = (family?.children || [])
    .filter((child) => child?.member_id)
    .map((child) => ({
      member_id: child.member_id,
      label: `자녀 · ${child.nickname || child.name || child.member_id}`,
    }));
  return [...parentOption, ...children];
};

export default function PassportTestPage() {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('issue');

  // ── 지점 ────────────────────────────────────────────────
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState('');
  const [storesError, setStoresError] = useState(null);

  // ── 발급 ────────────────────────────────────────────────
  const [issuing, setIssuing] = useState(false);
  const [issueError, setIssueError] = useState(null);
  // 이번 실행에서 발급한 결과(`POST /issue` 응답 전체). 새로고침하면 사라진다 —
  // 화면에 남는 것은 아래 `batch`(서버 재조회로 복원되는 값)다.
  const [issueResult, setIssueResult] = useState(null);

  // ── 코드 목록 ───────────────────────────────────────────
  // `batch` 는 **`GET /codes` 응답**에서만 채운다.
  const [batch, setBatch] = useState(null);
  const [codes, setCodes] = useState([]);
  const [codesLoading, setCodesLoading] = useState(false);
  const [codesError, setCodesError] = useState(null);

  // ── 인쇄 ────────────────────────────────────────────────
  // 여권 주소(서버의 `WEBAPP_BASE_URL`). **서버 응답에서만** 채우고 절대 저장하지 않는다 —
  // `localStorage` 에 캐시하면 서버 값과 조용히 갈라지는 C11 그 자체가 된다(E-4).
  // 발급 응답과 코드 목록 응답이 **둘 다** 이 값을 싣는 이유는, 브라우저를 닫았다 연
  // 재진입 경로(E8)에는 발급 응답이 없기 때문이다.
  const [webappBase, setWebappBase] = useState(null);
  const [printing, setPrinting] = useState(false);
  const [printResult, setPrintResult] = useState(null);
  // 버튼 문구를 '인쇄' → '재출력' 로 바꾸는 유일한 근거. 실패한 시도도 포함한다 —
  // 인쇄 실패는 발행을 되돌리지 않으므로 다음 누름은 어느 쪽이든 재출력이다(§4.3).
  const [printAttempted, setPrintAttempted] = useState(false);

  // ── 부여 (5콜 · §4.4) ───────────────────────────────────
  const [phone, setPhone] = useState('');
  const [grantCode, setGrantCode] = useState('');
  const [stepStates, setStepStates] = useState(IDLE_STEPS);
  const [preparing, setPreparing] = useState(false);
  const [granting, setGranting] = useState(false);
  // ②가 돌려준 **고객의** 모바일 access token.
  // 🔴 `localStorage` 에 넣지 않는다 — 스태프 단말에 고객 신원의 토큰을 남길 이유가 없고,
  //    새로고침하면 ①부터 다시 타는 것이 옳다(티켓은 어차피 1회성이다 — E7).
  const [mobileToken, setMobileToken] = useState(null);
  const [mobileMemberId, setMobileMemberId] = useState(null);
  const [validation, setValidation] = useState(null);
  const [targets, setTargets] = useState([]);
  const [targetMemberId, setTargetMemberId] = useState('');
  const [grantResult, setGrantResult] = useState(null);

  /** `batchId` 가 없으면 `batch_id` 를 빼고 부른다 = 서버가 최신 TEST 배치를 돌려준다(§4.3.1). */
  const requestCodes = useCallback(async (batchId) => {
    return api.get('/tools/passport-test/codes', {
      params: batchId ? { batch_id: batchId } : {},
    });
  }, []);

  const loadCodes = useCallback(async (batchId) => {
    setCodesLoading(true);
    setCodesError(null);
    try {
      let res;
      try {
        res = await requestCodes(batchId);
      } catch (err) {
        // 보관해 둔 배치가 서버에 없는 경우(DB 교체·배치 폐기)까지 404 로 굳지 않게 한다.
        // 이 화면에는 배치를 고르는 다른 수단이 없어서, 굳으면 재발급 말고는 빠져나갈 길이 없다.
        if (!batchId || err?.status !== 404) throw err;
        localStorage.removeItem(STORAGE_KEY_BATCH_ID);
        res = await requestCodes(null);
      }

      // 발행 전 최초 진입은 오류가 아니다 — 서버가 200 + `batch_id: null` 로 답한다.
      // 그 경우에도 `webapp_base` 는 실려 오므로 인쇄 게이트는 배치 유무와 무관하게 선다.
      setWebappBase(res.webapp_base || null);
      setBatch(res.batch_id ? { batch_id: res.batch_id, label: res.label } : null);
      setCodes(res.codes || []);
      if (res.batch_id) {
        localStorage.setItem(STORAGE_KEY_BATCH_ID, String(res.batch_id));
      } else {
        localStorage.removeItem(STORAGE_KEY_BATCH_ID);
      }
    } catch (err) {
      setCodesError(errorText(err, '코드 목록을 불러오지 못했습니다'));
    } finally {
      setCodesLoading(false);
    }
  }, [requestCodes]);

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const res = await getStores();
        setStores(res.stores || []);
      } catch (err) {
        setStoresError(errorText(err, '지점 목록을 불러올 수 없습니다'));
      }
      // 보관된 `batch_id` 가 없어도 호출한다 — 그 경우가 바로 최신 TEST 배치 회수 경로다(E8).
      const savedBatchId = localStorage.getItem(STORAGE_KEY_BATCH_ID);
      await loadCodes(savedBatchId ? Number(savedBatchId) : null);
    };
    bootstrap();
  }, [loadCodes]);

  const handleIssue = useCallback(async () => {
    if (!storeId) {
      setIssueError('발행할 지점을 먼저 선택하세요');
      return;
    }
    setIssuing(true);
    setIssueError(null);
    setIssueResult(null);
    // 새 배치는 아직 한 장도 인쇄되지 않았다 — 직전 배치의 인쇄 결과를 끌고 가지 않는다.
    setPrintResult(null);
    setPrintAttempted(false);
    try {
      // 수량은 보내지 않는다 — 서버가 10 으로 고정하며 `quantity` 를 실으면 422 다.
      const res = await api.post('/tools/passport-test/issue', { store_id: storeId });
      setIssueResult(res);
      setWebappBase(res.webapp_base || null);
      // 코드 10건은 발급 응답에 없다(`sample_code` 1건뿐) — 반드시 별도 조회한다(§4.3.1).
      await loadCodes(res.batch_id);
    } catch (err) {
      // E1 — `coupon_on=0` 지점의 400, TEST 아닌 서버의 403 모두 여기로 온다. 문구를 만들지 않는다.
      setIssueError(errorText(err, '발급에 실패했습니다'));
    } finally {
      setIssuing(false);
    }
  }, [storeId, loadCodes]);

  // 서버가 준 여권 주소가 localhost 인가. 게이트와 화면 강조가 **같은 판정**을 쓴다 —
  // 둘이 갈라지면 빨갛게 표시되는데 눌리거나, 눌리지 않는데 멀쩡해 보인다.
  // 소문자로 접어 비교하는 것은 넓게 막기 위해서다(막는 쪽이 안전한 판정이다).
  const baseIsLocalhost = !!webappBase && String(webappBase).toLowerCase().includes('localhost');

  /**
   * 인쇄를 막는 사유. `null` 이면 인쇄 가능.
   *
   * 🔴 **모르면 막는다(fail-safe).** 서버 값을 못 읽은 상태에서 인쇄하면 라벨의 QR 이 어떤
   *    호스트로 구워지는지 화면이 보증할 수 없고, 잘못된 라벨은 스캔 시점에야 드러난다.
   */
  const printBlockedReason = useMemo(() => {
    if (!batch || codes.length === 0) return '인쇄할 배치가 없습니다. 먼저 [10장 발급] 을 누르세요.';
    if (!webappBase) return '서버가 내려준 여권 주소를 확인하지 못했습니다. 코드 목록을 다시 불러오세요.';
    if (baseIsLocalhost) {
      return `여권 주소가 localhost 입니다 (${webappBase}) — 지금 인쇄하면 폰으로 열 수 없는 라벨이 나옵니다. 서버의 WEBAPP_BASE_URL 을 먼저 확인하세요.`;
    }
    return null;
  }, [batch, codes.length, webappBase, baseIsLocalhost]);

  const handlePrint = useCallback(async () => {
    // 게이트를 버튼 `disabled` 에만 걸지 않는다 — 핸들러에서도 끊는다(비활성 버튼에 클릭이
    // 도달하는 경로가 생겨도 라벨이 나가지 않아야 한다).
    if (printBlockedReason || printing) return;

    setPrinting(true);
    setPrintResult(null);
    try {
      // 코드 목록은 보내지 않는다 — 서버가 `batch_id` 로 다시 조회한다(P-4).
      const res = await printPassportTestBatch(batch.batch_id);
      if (res.success === false) {
        // 브릿지 미연결·USB 전송 실패는 예외가 아니라 `success:false` 로 온다(`printBandQr` 관례).
        setPrintResult({ ok: false, message: res.message || '출력에 실패했습니다' });
      } else {
        setPrintResult({
          ok: true,
          message: res.message || `여권 QR ${res.printed_count}장 출력`,
          printerName: res.printer_name,
        });
      }
    } catch (err) {
      // 403(비-TEST 서버)·404(배치 없음)·400(프린터 미설정)이 모두 여기로 온다. 문구를 만들지 않는다.
      setPrintResult({ ok: false, message: errorText(err, '출력 중 오류가 발생했습니다') });
    } finally {
      setPrinting(false);
      setPrintAttempted(true);
    }
  }, [printBlockedReason, printing, batch]);

  // ── 부여 5콜 (§4.4) ─────────────────────────────────────

  const setStep = useCallback((id, status, detail = null) => {
    setStepStates((prev) => ({ ...prev, [id]: { status, detail } }));
  }, []);

  /**
   * 한 콜을 실행하고 성공/실패를 **그 단계에** 기록한다.
   *
   * 🔴 실패하면 호출부가 체인을 **거기서 멈춘다.** 다음 단계를 타지 않는 것이 이 도구의
   *    핵심이다 — 어디서 끊겼는지가 화면에 그대로 남아야 한다(기획 §4.4). 실패를 삼키고
   *    다음 콜로 넘어가면 마지막 단계의 오류만 보이고 진짜 원인은 사라진다.
   */
  const runStep = useCallback(async (id, fallbackMessage, call, describe) => {
    setStep(id, 'running');
    try {
      const value = await call();
      setStep(id, 'ok', describe ? describe(value) : null);
      return { ok: true, value };
    } catch (err) {
      setStep(id, 'fail', grantErrorText(err, fallbackMessage));
      return { ok: false, value: null };
    }
  }, [setStep]);

  /**
   * 부여 화면을 실행 전 상태로 되돌린다.
   * 다시 누르면 직전 결과를 **전부** 버린다 — ②의 티켓이 1회성이라(E7) 어차피 ①부터 새로
   * 타므로, 옛 토큰·옛 검증결과·옛 대상이 한 칸이라도 남으면 어느 실행의 결과인지 알 수 없다.
   */
  const resetGrantChain = useCallback(() => {
    setStepStates(IDLE_STEPS);
    setMobileToken(null);
    setMobileMemberId(null);
    setValidation(null);
    setTargets([]);
    setTargetMemberId('');
    setGrantResult(null);
  }, []);

  /** ①~④ — 로그인 티켓 → 토큰 교환 → QR 사전검증 → 가족 조회. ⑤ 는 대상 선택 후 별도 버튼이다. */
  const handlePrepare = useCallback(async () => {
    const trimmedPhone = phone.trim();
    const trimmedCode = grantCode.trim();
    if (preparing || granting || !trimmedPhone || !trimmedCode) return;

    resetGrantChain();
    setPreparing(true);

    try {
      const login = await runStep('login', '로그인 티켓 발급에 실패했습니다', async () => {
        const res = await grantHttp.post('/mobile-auth/dev-login-by-phone', { phone: trimmedPhone });
        // 언랩이 없다 — `res.data` 가 서버 본문 그대로다.
        const ticket = res.data?.ticket;
        if (!ticket) throw new Error('응답에 ticket 이 없습니다');
        return ticket;
      }, (ticket) => `ticket ${maskPreview(ticket)}`);
      if (!login.ok) return;

      const exchanged = await runStep('exchange', '토큰 교환에 실패했습니다', async () => {
        const res = await grantHttp.post('/mobile-auth/exchange', { ticket: login.value });
        const token = res.data?.access_token;
        if (!token) throw new Error('응답에 access_token 이 없습니다');
        return { token, memberId: res.data?.member_id || null };
      }, (v) => `회원 ${v.memberId || '?'} · access_token ${maskPreview(v.token)}`);
      if (!exchanged.ok) return;
      setMobileToken(exchanged.value.token);
      setMobileMemberId(exchanged.value.memberId);

      // 🔴 ③④⑤ 에 실리는 유일한 자격증명. STAFF JWT 가 아니라 ②가 돌려준 값이다(V11).
      const auth = { headers: { Authorization: `Bearer ${exchanged.value.token}` } };

      const validated = await runStep('validate', 'QR 사전검증에 실패했습니다', async () => {
        const res = await grantHttp.post('/app/passport-registrations/validate', { code: trimmedCode }, auth);
        return res.data;
      }, (v) => `${v.result} · ${v.store_name || v.store_id || '-'}`);
      if (!validated.ok) return;
      setValidation(validated.value);

      const family = await runStep('family', '가족 조회에 실패했습니다', async () => {
        const res = await grantHttp.get('/app/family', auth);
        return res.data;
      }, (v) => `본인 1명 + 자녀 ${(v.children || []).length}명`);
      if (!family.ok) return;

      const options = buildTargets(family.value);
      setTargets(options);
      setTargetMemberId(options[0]?.member_id || '');
    } finally {
      setPreparing(false);
    }
  }, [phone, grantCode, preparing, granting, runStep, resetGrantChain]);

  /** ⑤ — 앱의 등록 엔드포인트. 대상 선택이 끝난 뒤에만 누를 수 있다. */
  const handleGrant = useCallback(async () => {
    if (preparing || granting || !mobileToken || !targetMemberId) return;
    setGranting(true);
    setGrantResult(null);
    try {
      const auth = { headers: { Authorization: `Bearer ${mobileToken}` } };
      const registered = await runStep('register', '여권 부여에 실패했습니다', async () => {
        const res = await grantHttp.post(
          '/app/passport-registrations',
          { code: grantCode.trim(), target_member_id: targetMemberId },
          auth,
        );
        return res.data;
      }, (v) => `${v.result} · 여권 ${v.passport_id}`);
      if (!registered.ok) return;

      setGrantResult(registered.value);
      // 부여 결과를 **원장으로 되돌려** 확인한다 — 코드가 `등록됨` 으로 바뀌는지가 이 체인의
      // 진짜 결과다(DoD). 재조회가 실패해도 부여는 이미 끝났으므로 부여 결과를 지우지 않는다.
      if (batch?.batch_id) await loadCodes(batch.batch_id);
    } finally {
      setGranting(false);
    }
  }, [preparing, granting, mobileToken, targetMemberId, grantCode, runStep, batch, loadCodes]);

  return (
    <div className="ptp-page">
      <header className="ptp-header">
        <button type="button" className="ptp-btn-back" onClick={() => navigate('/')}>← 메뉴</button>
        <div className="ptp-title">
          <h1>🛂 여권테스트</h1>
          <StoreBadge />
        </div>
        <span className="ptp-badge-test">TEST 전용</span>
      </header>

      <nav className="ptp-tabs" role="tablist" aria-label="여권테스트 탭">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`ptp-tab ${activeTab === tab.id ? 'ptp-tab-active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <main className="ptp-body">
        {activeTab === 'issue' && (
          <section className="ptp-panel" role="tabpanel" aria-label="발급">
            <div className="ptp-card">
              <label className="ptp-label" htmlFor="ptp-store">발행 지점</label>
              <select
                id="ptp-store"
                className="ptp-select"
                value={storeId}
                onChange={(e) => setStoreId(e.target.value)}
                disabled={issuing || stores.length === 0}
              >
                <option value="">지점을 선택하세요</option>
                {stores.map((store) => (
                  <option key={store.store_id} value={store.store_id}>
                    {store.store_name} ({store.store_id})
                  </option>
                ))}
              </select>
              {storesError && <p className="ptp-inline-err" role="alert">{storesError}</p>}
              <p className="ptp-hint">
                여권 패키지가 꺼져 있는 지점은 <b>발행 시점에 서버가 거절</b>합니다.
                지점 목록 API 가 그 설정을 내려주지 않아 미리 가려낼 수 없습니다.
              </p>

              <button
                type="button"
                className="ptp-btn-primary"
                onClick={handleIssue}
                disabled={issuing || !storeId}
              >
                {issuing ? '발급 중…' : '10장 발급'}
              </button>

              {issueError && (
                <div className="ptp-alert ptp-alert-err" role="alert">
                  <span className="ptp-alert-icon">⚠️</span>
                  <span>{issueError}</span>
                </div>
              )}

              {issueResult && (
                <div className="ptp-alert ptp-alert-ok">
                  <span className="ptp-alert-icon">✅</span>
                  <div>
                    <p className="ptp-alert-msg">
                      배치 #{issueResult.batch_id} · {issueResult.label} — {issueResult.issued}장 발행
                    </p>
                    <p className="ptp-alert-sub">
                      지점 {issueResult.store_name || issueResult.store_id}
                    </p>
                  </div>
                </div>
              )}

              {/* 서버가 경고를 달아 보냈으면 그대로 보여준다 — 삼키면 쿠폰 0장 발급을 인쇄 후에야 안다. */}
              {issueResult && issueResult.package_ok === false && issueResult.package_warning && (
                <div className="ptp-alert ptp-alert-warn" role="alert">
                  <span className="ptp-alert-icon">⚠️</span>
                  <span>{issueResult.package_warning}</span>
                </div>
              )}
            </div>

            <div className="ptp-card">
              <div className="ptp-codes-head">
                <h2>발행 코드</h2>
                {batch && (
                  <span className="ptp-codes-meta">
                    배치 #{batch.batch_id} · {batch.label} · {codes.length}건
                  </span>
                )}
              </div>

              {codesError && <p className="ptp-inline-err" role="alert">{codesError}</p>}

              {codesLoading && <p className="ptp-muted">코드 목록을 불러오는 중…</p>}

              {!codesLoading && !batch && !codesError && (
                <p className="ptp-muted">
                  아직 발행한 테스트 배치가 없습니다. 지점을 고르고 <b>10장 발급</b>을 누르세요.
                </p>
              )}

              {!codesLoading && batch && codes.length > 0 && (
                <div className="ptp-table-wrap">
                  <table className="ptp-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>코드</th>
                        <th>상태</th>
                        <th>등록 회원</th>
                        <th>여권 주소</th>
                      </tr>
                    </thead>
                    <tbody>
                      {codes.map((row, index) => (
                        <tr key={row.code}>
                          <td className="ptp-td-num">{index + 1}</td>
                          <td className="ptp-td-code">{row.code}</td>
                          <td>
                            <span className={`ptp-status ptp-status-${row.status}`}>
                              {CODE_STATUS_LABELS[row.status] || row.status}
                            </span>
                          </td>
                          <td className="ptp-td-member">{row.registered_member_id || '-'}</td>
                          <td className="ptp-td-url">{row.passport_url}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* ── 인쇄 (§4.3) — 발급과 **별도 조작**이다. 발급이 인쇄로 자동 이어지지 않는다:
                   인쇄 실패가 발행을 되돌린다는 오해를 막고, 실패 지점을 눈에 보이게 남긴다. */}
            <div className="ptp-card">
              <h2>인쇄</h2>

              {/* 🔴 서버가 내려준 값을 그대로 보여준다 — 화면이 자체 계산하지 않는다(C11). */}
              <p className="ptp-kv">
                <span className="ptp-kv-key">여권 주소 (서버 값)</span>
                <span
                  className={`ptp-kv-val ${baseIsLocalhost ? 'ptp-kv-val-bad' : ''}`}
                  data-testid="ptp-webapp-base"
                >
                  {webappBase || '(확인 안 됨)'}
                </span>
              </p>
              <p className="ptp-hint">
                라벨의 QR 에는 <b>이 주소로 시작하는 여권 링크</b>가 들어갑니다. 인쇄한 뒤에는
                주소를 바꿔도 이미 나온 라벨은 고칠 수 없습니다.
              </p>

              <button
                type="button"
                className="ptp-btn-primary"
                data-testid="ptp-print-btn"
                onClick={handlePrint}
                disabled={printing || !!printBlockedReason}
              >
                {printing ? '출력 중…' : (printAttempted ? '재출력' : '인쇄')}
              </button>

              {printBlockedReason && (
                <p className="ptp-inline-err" data-testid="ptp-print-blocked" role="alert">
                  {printBlockedReason}
                </p>
              )}

              {printResult && (
                <div
                  className={`ptp-alert ${printResult.ok ? 'ptp-alert-ok' : 'ptp-alert-err'}`}
                  data-testid="ptp-print-result"
                >
                  <span className="ptp-alert-icon">{printResult.ok ? '✅' : '⚠️'}</span>
                  <div>
                    <p className="ptp-alert-msg">{printResult.message}</p>
                    {printResult.ok && printResult.printerName && (
                      <p className="ptp-alert-sub">프린터 {printResult.printerName}</p>
                    )}
                    {!printResult.ok && (
                      <p className="ptp-alert-sub">
                        발행한 코드는 그대로 남아 있습니다. 원인을 고친 뒤 <b>재출력</b>을 누르세요.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* §7 L-3 공시 — 조용히 어긋나게 두지 않는다. */}
              <p className="ptp-hint">
                ⚠️ 테스트 인쇄는 <b>인쇄 이력과 용지 사용량에 기록되지 않습니다.</b> 운영 소모품
                통계를 오염시키지 않으려는 선택이며, 그만큼 프린터 관리 화면의 잔량이 실제와
                어긋납니다.
              </p>
            </div>
          </section>
        )}

        {activeTab === 'grant' && (
          <section className="ptp-panel" role="tabpanel" aria-label="부여">
            <div className="ptp-card">
              <h2>부여 대상</h2>
              <p className="ptp-hint">
                앱이 쓰는 <b>바로 그 엔드포인트</b>를 앱과 같은 순서로 호출합니다(R4).
                ①②는 앱의 소셜 로그인을 대신하는 dev 전용 준비 단계이고, ③④⑤가 앱 경로입니다.
              </p>

              <label className="ptp-label" htmlFor="ptp-phone">전화번호 (이미 가입한 회원)</label>
              <input
                id="ptp-phone"
                className="ptp-input"
                data-testid="ptp-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="01012345678"
                inputMode="numeric"
                autoComplete="off"
                disabled={preparing || granting}
              />
              <p className="ptp-hint">
                ①은 <b>PARENT/SINGLE + ACTIVE</b> 회원만 찾습니다. 없는 번호면 ①에서 끊깁니다(E3).
              </p>

              <label className="ptp-label" htmlFor="ptp-grant-code">여권 코드</label>
              {codes.length > 0 && (
                <select
                  className="ptp-select"
                  aria-label="발행 코드에서 선택"
                  data-testid="ptp-code-pick"
                  value={codes.some((row) => row.code === grantCode) ? grantCode : ''}
                  onChange={(e) => setGrantCode(e.target.value)}
                  disabled={preparing || granting}
                >
                  <option value="">발행 코드에서 선택…</option>
                  {codes.map((row) => (
                    <option key={row.code} value={row.code}>
                      {row.code} ({CODE_STATUS_LABELS[row.status] || row.status})
                    </option>
                  ))}
                </select>
              )}
              <input
                id="ptp-grant-code"
                className="ptp-input"
                data-testid="ptp-grant-code"
                value={grantCode}
                onChange={(e) => setGrantCode(e.target.value)}
                placeholder="ZLP_… 또는 https://…/passports/ZLP_…"
                autoComplete="off"
                disabled={preparing || granting}
              />
              <p className="ptp-hint">
                코드 원문이든 여권 URL 이든 <b>스캔값 그대로</b> 보내면 서버가 처리합니다 — 앱과 같습니다.
              </p>

              <button
                type="button"
                className="ptp-btn-primary"
                data-testid="ptp-prepare-btn"
                onClick={handlePrepare}
                disabled={preparing || granting || !phone.trim() || !grantCode.trim()}
              >
                {preparing ? '진행 중…' : '① ~ ④ 실행 (로그인 · 사전검증 · 가족조회)'}
              </button>

              {/* E5 공시 — 이 비대칭을 모르면 "발급은 403 인데 부여는 되네" 를 버그로 오인한다. */}
              <p className="ptp-hint">
                ⚠️ 이 탭의 5콜은 <b>여권테스트의 TEST 서버 게이트를 지나지 않습니다.</b> ①은
                운영 환경이 아니면 열리고 ③④⑤는 앱과 같은 공개 경로라, 발급·인쇄가 403 인
                서버에서도 부여만 동작할 수 있습니다.
              </p>
            </div>

            <div className="ptp-card">
              <h2>진행 단계</h2>
              <p className="ptp-hint">
                실패하면 <b>그 자리에서 멈춥니다.</b> 다음 단계로 넘어가지 않으므로 어디서
                끊겼는지가 그대로 남습니다.
              </p>
              <ol className="ptp-steps">
                {GRANT_STEPS.map((step) => {
                  const state = stepStates[step.id];
                  return (
                    <li
                      key={step.id}
                      className={`ptp-step ptp-step-${state.status}`}
                      data-testid={`ptp-step-${step.id}`}
                    >
                      <span className="ptp-step-icon">{STEP_ICONS[state.status]}</span>
                      <div className="ptp-step-body">
                        <p className="ptp-step-title">
                          {step.seq} {step.title}
                          <span className="ptp-step-auth">{step.auth}</span>
                        </p>
                        <p className="ptp-step-call">{step.call}</p>
                        {state.detail && <p className="ptp-step-detail">{state.detail}</p>}
                      </div>
                    </li>
                  );
                })}
              </ol>
              {mobileToken && (
                <p className="ptp-kv">
                  <span className="ptp-kv-key">③④⑤ 에 실리는 Authorization (②의 access_token)</span>
                  <span className="ptp-kv-val" data-testid="ptp-mobile-token">
                    Bearer {maskPreview(mobileToken)} · 회원 {mobileMemberId || '?'}
                  </span>
                </p>
              )}
            </div>

            {validation && (
              <div className="ptp-card">
                <h2>③ 사전검증 결과</h2>
                <p className="ptp-hint">읽기 전용 호출이라 여러 번 눌러도 안전합니다 — 상태를 바꾸지 않습니다.</p>
                <p className="ptp-kv">
                  <span className="ptp-kv-key">판정</span>
                  <span className="ptp-kv-val" data-testid="ptp-validate-result">{validation.result}</span>
                </p>
                <p className="ptp-kv">
                  <span className="ptp-kv-key">지점</span>
                  <span className="ptp-kv-val">{validation.store_name || '-'} ({validation.store_id})</span>
                </p>
                <p className="ptp-kv">
                  <span className="ptp-kv-key">여권 표지 이미지 (앱이 보는 값)</span>
                  <span className="ptp-kv-val">{validation.passport_product_front_img || '(없음)'}</span>
                </p>

                {/* E11 — 진행 여부는 사용자가 정한다. 화면이 대신 막지 않는다. */}
                {validation.result === 'already_registered' && (
                  <div className="ptp-alert ptp-alert-warn" role="alert" data-testid="ptp-already-registered">
                    <span className="ptp-alert-icon">⚠️</span>
                    <div>
                      <p className="ptp-alert-msg">이미 등록된 코드입니다.</p>
                      <p className="ptp-alert-sub">
                        소유자 {validation.member_id || '?'} · 여권 {validation.passport_id || '?'}.
                        그대로 ⑤를 눌러도 <b>새 여권이 생기지 않고</b> 같은 결과가 돌아옵니다(멱등).
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {targets.length > 0 && (
              <div className="ptp-card">
                <h2>⑤ 대상 선택 · 부여</h2>
                <label className="ptp-label" htmlFor="ptp-target">부여 대상 (본인 또는 ACTIVE 자녀)</label>
                <select
                  id="ptp-target"
                  className="ptp-select"
                  data-testid="ptp-target"
                  value={targetMemberId}
                  onChange={(e) => setTargetMemberId(e.target.value)}
                  disabled={granting}
                >
                  {targets.map((target) => (
                    <option key={target.member_id} value={target.member_id}>
                      {target.label} ({target.member_id})
                    </option>
                  ))}
                </select>
                {targets.length === 1 && (
                  <p className="ptp-hint">자녀가 없어 <b>본인</b>만 후보입니다 — 오류가 아닙니다(E10).</p>
                )}

                <button
                  type="button"
                  className="ptp-btn-primary"
                  data-testid="ptp-grant-btn"
                  onClick={handleGrant}
                  disabled={preparing || granting || !targetMemberId}
                >
                  {granting ? '부여 중…' : '⑤ 여권 부여'}
                </button>

                {grantResult && (
                  <div className="ptp-alert ptp-alert-ok" data-testid="ptp-grant-result">
                    <span className="ptp-alert-icon">✅</span>
                    <div>
                      <p className="ptp-alert-msg">
                        {grantResult.result === 'registered'
                          ? '여권을 부여했습니다'
                          : '이미 등록된 여권입니다 — 새로 만들지 않았습니다(멱등)'}
                      </p>
                      <p className="ptp-alert-sub">
                        여권 {grantResult.passport_id} · 대상 {grantResult.member_id} ·
                        쿠폰 {grantResult.issued_coupon_count}장
                      </p>
                      {/* 금액은 서버가 DB 설정에서 계산해 내려준 값 그대로다 — 화면이 만들지 않는다. */}
                      {(grantResult.backfilled?.coins || []).length > 0 && (
                        <p className="ptp-alert-sub">
                          소급 주화: {grantResult.backfilled.coins
                            .map((coin) => `${coin.reason} ${coin.amount}`)
                            .join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {activeTab === 'cancel' && (
          <section className="ptp-panel" role="tabpanel" aria-label="취소안내">
            <div className="ptp-card">
              <h2>여권취소</h2>
              <p className="ptp-muted">
                취소는 <b>기존 여권취소 화면</b>을 그대로 사용합니다. 이 도구에는 별도의 취소 기능이 없습니다.
              </p>
              <button
                type="button"
                className="ptp-btn-primary"
                onClick={() => navigate('/passport-cancel')}
              >
                여권취소 화면 열기
              </button>
              <div className="ptp-alert ptp-alert-warn">
                <span className="ptp-alert-icon">⚠️</span>
                <div>
                  <p className="ptp-alert-msg">취소해도 QR 은 재사용되지 않습니다.</p>
                  <p className="ptp-alert-sub">
                    취소는 여권만 되돌리고 QR 은 <b>등록됨</b> 상태로 남습니다. 다시 테스트하려면
                    발급 탭에서 배치를 새로 발행하세요.
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
